import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from 'antd';

/**
 * AppButton — shared button for the redesigned UI, built on the AntD Button
 * (loading spinner, disabled handling, icon spacing, htmlType, href) with
 * Tailwind variants so every screen uses the same look.
 *
 * Variants:
 *  - primary    Solid purple                   ("I'm interested", "Search", "Edit profile")
 *  - secondary  Purple outline on white        ("Refer someone", "View PDF")
 *  - success    Green outline on white         ("See match score", "Interested")
 *  - neutral    Gray outline on white          ("Cancel")
 *  - danger     Red outline on white           (destructive actions)
 *  - link       No border / background         ("Clear filters", "Withdraw interest")
 *
 * Sizes: sm (32px, text-xs) · md (40px, text-sm) · lg (44px, text-sm)
 *
 * Navigation:
 *  - `to`   renders a react-router <Link> with identical styling (client-side routing)
 *  - `href` renders an AntD link button (<a>); pass target="_blank" for new tabs
 *
 * Usage:
 *   <AppButton variant="primary" block onClick={...}>I'm interested</AppButton>
 *   <AppButton variant="secondary" icon={<EyeOutlined />} href={url} target="_blank">View PDF</AppButton>
 *   <AppButton variant="link" size="sm" to="/talent/job-board">View jobs</AppButton>
 */

const BASE_CLASSES = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-solid font-semibold leading-none shadow-none transition-colors hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

export const BUTTON_VARIANTS = {
    primary: 'border-primary bg-primary text-white hover:border-primary-dark hover:bg-primary-dark hover:text-white focus:border-primary focus:bg-primary focus:text-white',
    secondary: 'border-primary/30 bg-white text-primary hover:border-primary hover:bg-primary/5 hover:text-primary focus:border-primary/30 focus:bg-white focus:text-primary',
    success: 'border-green-600/30 bg-white text-green-700 hover:border-green-600 hover:bg-green-50 hover:text-green-700 focus:border-green-600/30 focus:bg-white focus:text-green-700',
    neutral: 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:border-gray-200 focus:bg-white focus:text-gray-700',
    danger: 'border-red-200 bg-white text-red-600 hover:border-red-500 hover:bg-red-50 hover:text-red-700 focus:border-red-200 focus:bg-white focus:text-red-600',
    link: 'border-transparent bg-transparent text-primary hover:border-transparent hover:bg-primary/5 hover:text-primary-dark focus:border-transparent focus:bg-transparent focus:text-primary',
};

export const BUTTON_SIZES = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 text-sm',
    lg: 'h-11 px-5 text-sm',
};

const DISABLED_CLASSES = 'cursor-not-allowed opacity-50';

export const appButtonClasses = ({ variant = 'primary', size = 'md', block = false, disabled = false, className = '' } = {}) => [
    BASE_CLASSES,
    BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
    BUTTON_SIZES[size] || BUTTON_SIZES.md,
    block ? 'flex w-full' : '',
    disabled ? DISABLED_CLASSES : '',
    className,
].filter(Boolean).join(' ');

const AppButton = React.forwardRef(({
    variant = 'primary',
    size = 'md',
    block = false,
    to,
    state,
    replace,
    icon,
    loading = false,
    disabled = false,
    className = '',
    children,
    ...props
}, ref) => {
    const classes = appButtonClasses({ variant, size, block, disabled: disabled || loading, className });

    // Client-side route: render a router Link (disabled links fall through to a disabled button)
    if (to && !disabled && !loading) {
        return (
            <Link ref={ref} to={to} state={state} replace={replace} className={classes} {...props}>
                {icon}
                {children}
            </Link>
        );
    }

    return (
        <Button
            ref={ref}
            type="default"
            icon={icon}
            loading={loading}
            disabled={disabled}
            className={classes}
            {...props}
        >
            {children}
        </Button>
    );
});

AppButton.displayName = 'AppButton';

AppButton.propTypes = {
    variant: PropTypes.oneOf(Object.keys(BUTTON_VARIANTS)),
    size: PropTypes.oneOf(Object.keys(BUTTON_SIZES)),
    block: PropTypes.bool,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    state: PropTypes.any,
    replace: PropTypes.bool,
    href: PropTypes.string,
    icon: PropTypes.node,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};

export default AppButton;
